const storageKey = 'goblog:post-view-counts'
const eventName = 'goblog:post-view-count'
const maxAge = 30 * 60 * 1000

export interface PostViewCountUpdate {
  postID: number
  viewCount: number
}

type StoredViewCounts = Record<string, { viewCount: number; updatedAt: number }>

function readStoredCounts(): StoredViewCounts {
  if (!import.meta.client) return {}
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(storageKey) || '{}') as StoredViewCounts
    const now = Date.now()
    return Object.fromEntries(Object.entries(parsed).filter(([id, value]) =>
      Number(id) > 0 && Number(value?.viewCount) >= 0 && now - Number(value?.updatedAt || 0) < maxAge
    ))
  } catch {
    return {}
  }
}

function writeStoredCounts(counts: StoredViewCounts) {
  try {
    window.sessionStorage.setItem(storageKey, JSON.stringify(counts))
  } catch {
    // Storage can be disabled in privacy modes. The current-page event still works.
  }
}

export function publishPostViewCount(update: PostViewCountUpdate) {
  if (!import.meta.client || !Number.isFinite(update.postID) || update.postID <= 0 || !Number.isFinite(update.viewCount)) return
  const postID = Math.trunc(update.postID)
  const viewCount = Math.max(0, Math.trunc(update.viewCount))
  const counts = readStoredCounts()
  const previous = counts[String(postID)]?.viewCount ?? -1
  if (viewCount < previous) return
  counts[String(postID)] = { viewCount, updatedAt: Date.now() }
  writeStoredCounts(counts)
  window.dispatchEvent(new CustomEvent<PostViewCountUpdate>(eventName, { detail: { postID, viewCount } }))
}

export function storedPostViewCounts(): PostViewCountUpdate[] {
  return Object.entries(readStoredCounts()).map(([postID, value]) => ({ postID: Number(postID), viewCount: value.viewCount }))
}

export const postViewCountEventName = eventName
